import { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MarkdownEditor } from "./MarkdownEditor";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CalendarIcon, Plus, X, Eye, ArrowLeft, MapPin, Clock, DollarSign, Briefcase, Tag, Paperclip, Trash2 } from "lucide-react";
import { format, addDays } from "date-fns";
import { cn } from "@/lib/utils";
import { ChallengeAttachmentUpload, ChallengeAttachment } from "./ChallengeAttachmentUpload";
import { ChallengeTemplatesModal } from "./ChallengeTemplatesModal";
import { ChallengeResourceLinksInput, ResourceLink } from "./ChallengeResourceLinks";
import { z } from "zod";
import { containsSpamContent, hasExcessiveLinks } from "@/lib/spamValidation";
import { useFormRateLimit, RateLimitEndpoints } from "@/hooks/useRateLimit";

const challengeSchema = z.object({
  title: z.string()
    .trim()
    .min(5, "Title must be at least 5 characters")
    .max(200, "Title must be less than 200 characters")
    .refine(val => !containsSpamContent(val), "Title contains prohibited content"),
  company: z.string()
    .trim()
    .min(2, "Company name must be at least 2 characters")
    .max(100, "Company name must be less than 100 characters")
    .refine(val => !containsSpamContent(val), "Company name contains prohibited content"),
  description: z.string()
    .trim()
    .min(50, "Description must be at least 50 characters")
    .max(20000, "Description must be less than 20000 characters")
    .refine(val => !containsSpamContent(val), "Description contains prohibited content")
    .refine(val => !hasExcessiveLinks(val), "Too many links in description"),
  reward: z.string()
    .trim()
    .min(1, "Please enter a reward")
    .max(100, "Reward must be less than 100 characters"),
  deadline: z.date({ required_error: "Please select a deadline" }),
});

export interface DuplicateChallengeData {
  title: string;
  company: string;
  description: string;
  type: "hire" | "solve" | "sell";
  reward: string;
  rewardValue?: number;
  location?: string;
  country?: string;
  is_remote?: boolean;
  tags?: string[];
}

interface AddChallengeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  onSuccess: () => void;
  duplicateFrom?: DuplicateChallengeData | null;
}

interface ChallengeTemplate {
  id: string;
  name: string;
  description: string;
  type: string;
  default_reward: string | null;
  default_deadline_days: number | null;
  default_tags: string[] | null;
  template_content: string | null;
}

interface SavedDraft {
  formData: {
    title: string;
    company: string;
    description: string;
    type: "hire" | "solve" | "sell";
    reward: string;
    rewardValue: number;
    location: string;
    country: string;
    is_remote: boolean;
    deadline: string | undefined;
  };
  tags: string[];
  attachments: ChallengeAttachment[];
  savedAt: string;
}

const DRAFT_STORAGE_KEY = "challenge_draft";

const challengeTypes = [
  { value: "hire", label: "Hiring Challenge", description: "Find talent through proven work" },
  { value: "solve", label: "Cash Bounty", description: "Pay for solutions to specific problems" },
  { value: "sell", label: "Commission Deal", description: "Reward successful sales/deals" },
];

const getInitialFormData = () => ({
  title: "",
  company: "",
  description: "",
  type: "solve" as "hire" | "solve" | "sell",
  reward: "",
  rewardValue: 0,
  location: "",
  country: "",
  is_remote: true,
  deadline: undefined as Date | undefined,
});

export const AddChallengeModal = ({
  open,
  onOpenChange,
  userId,
  onSuccess,
  duplicateFrom,
}: AddChallengeModalProps) => {
  const [loading, setLoading] = useState(false);
  const [showTemplates, setShowTemplates] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData());
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [attachments, setAttachments] = useState<ChallengeAttachment[]>([]);
  const [resources, setResources] = useState<ResourceLink[]>([]);
  const { checkFormRateLimit } = useFormRateLimit();

  // Handle duplication - pre-fill form when duplicateFrom changes
  useEffect(() => {
    if (duplicateFrom && open) {
      setFormData({
        title: `${duplicateFrom.title} (Copy)`,
        company: duplicateFrom.company,
        description: duplicateFrom.description,
        type: duplicateFrom.type,
        reward: duplicateFrom.reward,
        rewardValue: duplicateFrom.rewardValue || 0,
        location: duplicateFrom.location || "",
        country: duplicateFrom.country || "",
        is_remote: duplicateFrom.is_remote ?? true,
        deadline: addDays(new Date(), 14), // Default 14 days for duplicated challenges
      });
      setTags(duplicateFrom.tags || []);
      setShowTemplates(false); // Skip template selection when duplicating
    }
  }, [duplicateFrom, open]);

  // Load draft on mount
  useEffect(() => {
    const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (savedDraft) {
      try {
        const draft: SavedDraft = JSON.parse(savedDraft);
        setHasDraft(true);
      } catch (e) {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    }
  }, []);

  // Restore draft
  const restoreDraft = useCallback(() => {
    const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (savedDraft) {
      try {
        const draft: SavedDraft = JSON.parse(savedDraft);
        setFormData({
          ...draft.formData,
          deadline: draft.formData.deadline ? new Date(draft.formData.deadline) : undefined,
        });
        setTags(draft.tags);
        setAttachments(draft.attachments);
        setShowTemplates(false);
        toast.success("Draft restored");
      } catch (e) {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    }
  }, []);

  // Save draft whenever form changes
  useEffect(() => {
    if (!showTemplates && (formData.title || formData.description || formData.company || formData.reward)) {
      const draft: SavedDraft = {
        formData: {
          ...formData,
          deadline: formData.deadline?.toISOString(),
        },
        tags,
        attachments,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
      setHasDraft(true);
    }
  }, [formData, tags, attachments, showTemplates]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setHasDraft(false);
  }, []);

  // Ref to track if template was selected (avoids race condition with state)
  const templateSelectedRef = useRef(false);

  const handleSelectTemplate = (template: ChallengeTemplate) => {
    const deadlineDate = template.default_deadline_days 
      ? addDays(new Date(), template.default_deadline_days) 
      : undefined;

    setFormData({
      ...formData,
      type: (template.type as "hire" | "solve" | "sell") || "solve",
      reward: template.default_reward || "",
      description: template.template_content || "",
      deadline: deadlineDate,
    });
    setTags(template.default_tags || []);
    // Mark that template was selected before closing
    templateSelectedRef.current = true;
    setShowTemplates(false);
  };

  const resetForm = () => {
    setFormData(getInitialFormData());
    setTags([]);
    setTagInput("");
    setAttachments([]);
    setResources([]);
    setShowTemplates(true);
    setShowPreview(false);
  };

  const discardDraft = () => {
    clearDraft();
    resetForm();
    toast.success("Draft discarded");
  };

  const validateForm = (): boolean => {
    const result = challengeSchema.safeParse({
      title: formData.title,
      company: formData.company,
      description: formData.description,
      reward: formData.reward,
      deadline: formData.deadline,
    });

    if (!result.success) {
      // Show first error
      const firstError = result.error.errors[0];
      if (firstError) {
        toast.error(firstError.message);
      }
      return false;
    }

    return true;
  };

  const handlePreview = () => {
    if (validateForm()) {
      setShowPreview(true);
    }
  };

  const getChallengeTypeLabel = (type: string) => {
    return challengeTypes.find(t => t.value === type)?.label || type;
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      // Check rate limit
      const rateLimitResult = await checkFormRateLimit(RateLimitEndpoints.FORM_CHALLENGE);
      if (!rateLimitResult.allowed) {
        toast.error(rateLimitResult.message);
        setLoading(false);
        return;
      }

      const { data: challenge, error } = await supabase.from("challenges").insert({
        user_id: userId,
        title: formData.title.trim(),
        company: formData.company.trim(),
        description: formData.description.trim(),
        type: formData.type,
        reward: formData.reward.trim(),
        reward_value: formData.rewardValue || 0,
        location: formData.is_remote ? "Remote" : formData.location.trim() || null,
        country: formData.is_remote ? null : formData.country.trim() || null,
        is_remote: formData.is_remote,
        deadline: formData.deadline.toISOString(),
        tags: tags,
        attachments: attachments as unknown as any,
        resources: resources as unknown as any,
        is_active: true,
        is_new: true,
        is_approved: false,
        review_status: "not_started",
        submissions_count: 0,
      }).select().single();

      if (error) {
        toast.error("Failed to create challenge");
        console.error(error);
        setLoading(false);
        return;
      }

      // Trigger AI moderation
      const { data: moderationResult, error: moderationError } = await supabase.functions.invoke(
        'moderate-content',
        {
          body: {
            contentType: 'challenge',
            contentId: challenge.id,
            title: formData.title.trim(),
            content: formData.description.trim(),
          },
        }
      );

      if (moderationError) {
        console.error('Moderation error:', moderationError);
      }

      if (moderationResult?.approved) {
        toast.success("Challenge created and published!");
      } else {
        toast.success("Challenge created! It's being reviewed.");
      }

      clearDraft();
      resetForm();
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error("Failed to create challenge");
    } finally {
      setLoading(false);
    }
  };

  // Handle template modal close - only close main modal if user explicitly closes without selecting
  const handleTemplateModalClose = (isOpen: boolean) => {
    if (!isOpen) {
      if (templateSelectedRef.current) {
        // Template was selected, reset the ref for next time
        templateSelectedRef.current = false;
      } else {
        // User closed template modal without selecting - close entire modal
        resetForm();
        onOpenChange(false);
      }
    }
  };

  return (
    <>
      <ChallengeTemplatesModal
        open={open && showTemplates}
        onOpenChange={handleTemplateModalClose}
        onSelectTemplate={handleSelectTemplate}
        hasDraft={hasDraft}
        onRestoreDraft={restoreDraft}
        onDiscardDraft={discardDraft}
      />

      <Dialog open={open && !showTemplates && !showPreview} onOpenChange={(isOpen) => {
        if (!isOpen) {
          // Don't reset - just close (draft is auto-saved)
        }
        onOpenChange(isOpen);
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTemplates(true)}
                  className="text-muted-foreground hover:text-foreground -ml-2 h-8 px-2"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Templates</span>
                </Button>
                <DialogTitle className="text-base sm:text-lg">Create New Challenge</DialogTitle>
              </div>
              {hasDraft && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={discardDraft}
                  className="text-muted-foreground hover:text-destructive h-8 self-end sm:self-auto"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  <span className="text-xs sm:text-sm">Discard Draft</span>
                </Button>
              )}
            </div>
          </DialogHeader>

          <div className="space-y-4 sm:space-y-6 pt-2 sm:pt-4">
          {/* Challenge Type */}
          <div className="space-y-2">
            <Label className="text-sm">Challenge Type</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
              {challengeTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setFormData({ ...formData, type: type.value as "hire" | "solve" | "sell" })}
                  className={cn(
                    "p-2.5 sm:p-3 rounded-lg border text-left transition-colors",
                    formData.type === type.value
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <div className="font-medium text-sm text-foreground">{type.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 sm:mt-1 line-clamp-1 sm:line-clamp-none">{type.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="title" className="text-sm">Challenge Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Build an AI-Powered Lead Scoring System"
              maxLength={200}
              className="h-10"
            />
            <p className="text-xs text-muted-foreground text-right">{formData.title.length}/200</p>
          </div>

          {/* Company */}
          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="company" className="text-sm">Company/Organization *</Label>
            <Input
              id="company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              placeholder="Your company name"
              maxLength={100}
              className="h-10"
            />
            <p className="text-xs text-muted-foreground text-right">{formData.company.length}/100</p>
          </div>

          {/* Description */}
          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="description" className="text-sm">Description * <span className="text-muted-foreground font-normal text-xs">(min 50 characters)</span></Label>
            <MarkdownEditor
              id="description"
              value={formData.description}
              onChange={(value) => setFormData({ ...formData, description: value })}
              placeholder="Describe what you need, requirements, and deliverables..."
              rows={5}
              storageKey="add-challenge-description"
            />
            <p className="text-xs text-muted-foreground text-right">{formData.description.length}/20000</p>
          </div>

          {/* Reward */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="reward" className="text-sm">Reward (displayed)</Label>
              <Input
                id="reward"
                value={formData.reward}
                onChange={(e) => setFormData({ ...formData, reward: e.target.value })}
                placeholder="e.g., $5,000 or 10% Commission"
                className="h-10"
              />
            </div>
            <div className="space-y-1.5 sm:space-y-2">
              <Label htmlFor="rewardValue" className="text-sm">Reward Value (USD, for sorting)</Label>
              <Input
                id="rewardValue"
                type="number"
                value={formData.rewardValue || ""}
                onChange={(e) => setFormData({ ...formData, rewardValue: parseInt(e.target.value) || 0 })}
                placeholder="5000"
                className="h-10"
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_remote}
                onCheckedChange={(checked) => setFormData({ ...formData, is_remote: checked })}
              />
              <Label className="text-sm">Remote / Global</Label>
            </div>

            {!formData.is_remote && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="location" className="text-sm">City/State</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., San Francisco, CA"
                    className="h-10"
                  />
                </div>
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="country" className="text-sm">Country Code</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="e.g., US, UK, DE"
                    className="h-10"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Deadline */}
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-sm">Deadline</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal h-10",
                    !formData.deadline && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.deadline ? format(formData.deadline, "PPP") : "Select deadline"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.deadline}
                  onSelect={(date) => setFormData({ ...formData, deadline: date })}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Tags */}
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-sm">Tags/Skills</Label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Add a tag..."
                className="h-10"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={handleAddTag} className="h-10 px-3">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-secondary rounded-full text-xs sm:text-sm"
                  >
                    {tag}
                    <button onClick={() => handleRemoveTag(tag)} className="hover:text-destructive p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Attachments */}
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-sm">Reference Materials (Files)</Label>
            <ChallengeAttachmentUpload
              userId={userId}
              attachments={attachments}
              onAttachmentsChange={setAttachments}
            />
          </div>

          {/* Resource Links */}
          <div className="space-y-1.5 sm:space-y-2">
            <Label className="text-sm">External Links & Videos</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Add video demos, documentation, GitHub repos, or any relevant links
            </p>
            <ChallengeResourceLinksInput
              resources={resources}
              onResourcesChange={setResources}
            />
          </div>

          {/* Submit */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 pt-2 sm:pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handlePreview} className="w-full sm:w-auto">
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </Button>
          </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={open && showPreview} onOpenChange={(isOpen) => {
        if (!isOpen) {
          setShowPreview(false);
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <button 
                onClick={() => setShowPreview(false)}
                className="p-1 hover:bg-secondary rounded-md transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              Preview Challenge
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pt-4">
            {/* Challenge Type Badge */}
            <div className="flex items-center gap-2">
              <span className={cn(
                "px-3 py-1 rounded-full text-sm font-medium",
                formData.type === "hire" && "bg-blue-500/10 text-blue-500",
                formData.type === "solve" && "bg-green-500/10 text-green-500",
                formData.type === "sell" && "bg-purple-500/10 text-purple-500"
              )}>
                {getChallengeTypeLabel(formData.type)}
              </span>
            </div>

            {/* Title & Company */}
            <div>
              <h2 className="text-2xl font-bold text-foreground">{formData.title}</h2>
              <p className="text-muted-foreground mt-1 flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                {formData.company}
              </p>
            </div>

            {/* Key Details */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Reward</p>
                  <p className="font-semibold text-foreground">{formData.reward}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Location</p>
                  <p className="font-semibold text-foreground">
                    {formData.is_remote ? "Remote" : `${formData.location}${formData.country ? `, ${formData.country}` : ""}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-xs text-muted-foreground">Deadline</p>
                  <p className="font-semibold text-foreground">
                    {formData.deadline ? format(formData.deadline, "PPP") : "Not set"}
                  </p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Description</h3>
              <div className="p-4 bg-secondary/20 rounded-lg">
                <p className="text-foreground whitespace-pre-wrap">{formData.description}</p>
              </div>
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-secondary rounded-full text-sm text-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments */}
            {attachments.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachments ({attachments.length})
                </h3>
                <div className="space-y-2">
                  {attachments.map((attachment, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-secondary/20 rounded-lg">
                      <Paperclip className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-foreground">{attachment.name}</span>
                      <span className="text-xs text-muted-foreground">({attachment.size})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button onClick={handleSubmit} disabled={loading}>
                {loading ? "Creating..." : "Create Challenge"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
